import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {Map, AdvancedMarker, useMap} from '@vis.gl/react-google-maps';
import { IconLayer } from '@deck.gl/layers';
import { ScatterplotLayer, TextLayer, PolygonLayer } from '@deck.gl/layers';
import Supercluster from 'supercluster';
import { AppCTX } from '../data/AppContext';
import AreaManager from './AreaManager'
import MyLocationMarker from "./MyLocationMarker";
import DeckGlOverlay from './DeckGlOverlay';
import DemographicsOverlay from './DemographicsOverlay';
import AddHomeIcon from '@mui/icons-material/AddHome';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Stack from '@mui/material/Stack';
import Slide from '@mui/material/Slide';
import IconButton from '@mui/material/IconButton';
import { useDebouncedCallback } from 'use-debounce';
import isEqual from 'lodash.isequal';

// Add CSS for spinning animation
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = spinAnimation;
  document.head.appendChild(style);
}

const config = {
  'googleMapsApiKey': process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: ['places']
}

// Icon mapping for 8 statuses, each 40x40, left to right in spritesheet.png
const iconMapping = {
  bad_credit:      { x: 0,    y: 0, width: 40, height: 40, mask: false },
  ni:              { x: 40,   y: 0, width: 40, height: 40, mask: false },
  away:            { x: 80,   y: 0, width: 40, height: 40, mask: false },
  cb:              { x: 120,  y: 0, width: 40, height: 40, mask: false },
  solar_installed: { x: 160,  y: 0, width: 40, height: 40, mask: false }, // swapped
  alarm_installed: { x: 200,  y: 0, width: 40, height: 40, mask: false }, // swapped
  renter:          { x: 240,  y: 0, width: 40, height: 40, mask: false },
  default:         { x: 280,  y: 0, width: 40, height: 40, mask: false }
};

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 }; // San Francisco as example

// Memoized color conversion function
const hexToRgba = (hex, alpha) => {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b, alpha];
};

// Helper function to calculate polygon center for label positioning
const calculatePolygonCenter = (polygon) => {
  if (!polygon || polygon.length === 0) return null;
  
  let sumLat = 0;
  let sumLng = 0;
  
  for (const [lng, lat] of polygon) {
    sumLat += lat;
    sumLng += lng;
  }
  
  return {
    lat: sumLat / polygon.length,
    lng: sumLng / polygon.length
  };
};

// Helper for stable memoization with deep equality
function useStableMemo(factory, deps) {
  const ref = useRef();
  const value = factory();
  if (!ref.current || !isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

// Custom hook for map state management - isolated from main component
function useMapState() {
  const map = useMap();
  const [debouncedZoomLevel, setDebouncedZoomLevel] = useState(20);
  const [debouncedBounds, setDebouncedBounds] = useState(null);
  
  // Debounce zoom and bounds updates
  const debouncedUpdate = useDebouncedCallback((newZoom, newBounds) => {
    // Don't store in state - just update debounced values
    setDebouncedZoomLevel(newZoom);
    setDebouncedBounds(newBounds);
  }, 300); // 300ms debounce - increased to reduce frequency

  // Get real-time map values without storing in state
  const getMapCenter = useCallback(() => {
    if (!map) return DEFAULT_CENTER;
    const center = map.getCenter();
    return center ? { lat: center.lat(), lng: center.lng() } : DEFAULT_CENTER;
  }, [map]);

  const getZoomLevel = useCallback(() => {
    if (!map) return 20;
    return map.getZoom();
  }, [map]);

  const getBounds = useCallback(() => {
    if (!map) return null;
    const b = map.getBounds();
    if (!b) return null;
    return [
      b.getSouthWest().lng(), b.getSouthWest().lat(),
      b.getNorthEast().lng(), b.getNorthEast().lat()
    ];
  }, [map]);

  // Update debounced zoom/bounds when map changes - isolated logic
  useEffect(() => {
    if (!map) return;
    const boundsArr = getBounds();
    if (!boundsArr) return;
    
    console.log('🔄 MAPSTATE: Updating debounced bounds/zoom', {
      zoomLevel: getZoomLevel(),
      boundsArr
    });
    debouncedUpdate(getZoomLevel(), boundsArr);
  }, [map, getBounds, getZoomLevel, debouncedUpdate]);

  // Optimize zoom listener with useCallback
  const handleZoomChange = useCallback(() => {
    // Don't update state - just trigger debounced update
    const boundsArr = getBounds();
    if (boundsArr) {
      debouncedUpdate(getZoomLevel(), boundsArr);
    }
  }, [map, getBounds, getZoomLevel, debouncedUpdate]);

  // Listen for zoom changes - isolated from main component
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('zoom_changed', handleZoomChange);
    return () => listener && listener.remove();
  }, [map, handleZoomChange]);

  return {
    map,
    getMapCenter,
    getZoomLevel,
    getBounds,
    debouncedZoomLevel,
    debouncedBounds
  };
}

// Custom hook for clustering logic - isolated from main component
function useClustering(locations, debouncedZoomLevel, debouncedBounds) {
  // Prepare data for deck.gl layers - optimized with stable reference
  const clusteringMarkerData = useStableMemo(() =>
    locations
      .filter(lead => lead.coords && typeof lead.coords.lat === 'number' && typeof lead.coords.lng === 'number')
      .map(lead => ({
        position: [lead.coords.lng, lead.coords.lat],
        markerStatus: iconMapping[lead.markerStatus] ? lead.markerStatus : 'default',
        address: lead.address || 'Prospect',
        data: lead
      })),
    [locations]
  );

  // Prepare geojson points for supercluster - optimized
  const geojsonPoints = useMemo(() =>
    clusteringMarkerData.map(d => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: d.position },
      properties: { ...d }
    })),
    [clusteringMarkerData]
  );

  // Create and load supercluster - optimized with better caching
  const clusteringSupercluster = useMemo(() => {
    if (geojsonPoints.length === 0) return null;
    const cluster = new Supercluster({
      radius: 60,
      maxZoom: 20
    });
    cluster.load(geojsonPoints);
    return cluster;
  }, [geojsonPoints]);

  // Get clusters for debounced bounds and zoom
  const clusters = useMemo(() => {
    if (!debouncedBounds || !clusteringSupercluster) return [];
    return clusteringSupercluster.getClusters(debouncedBounds, Math.round(debouncedZoomLevel));
  }, [clusteringSupercluster, debouncedBounds, debouncedZoomLevel]);

  // Split clusters and points - memoized
  const { clusteringClusterPoints, clusteringSinglePoints } = useMemo(() => {
    const clusteringClusterPoints = clusters.filter(f => f.properties.cluster);
    const clusteringSinglePoints = clusters.filter(f => !f.properties.cluster);
    return { clusteringClusterPoints, clusteringSinglePoints };
  }, [clusters]);

  return {
    markerData: clusteringMarkerData,
    geojsonPoints,
    supercluster: clusteringSupercluster,
    clusters,
    clusterPoints: clusteringClusterPoints,
    singlePoints: clusteringSinglePoints
  };
}

// Custom hook for demographics state management
function useDemographicsState() {
  const [demographicsActive, setDemographicsActive] = useState(false);

  return {
    demographicsActive,
    setDemographicsActive
  };
}

// Memoized demographics components
const MemoizedDemographicsOverlay = React.memo(DemographicsOverlay);

// Memoized crosshair component
const DemographicsCrosshair = React.memo(() => (
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '20px',
      height: '20px',
      pointerEvents: 'none',
      zIndex: 1500
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '20px',
        height: '2px',
        backgroundColor: 'white',
        boxShadow: '0 0 4px rgba(0,0,0,0.8)'
      }}
    />
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '2px',
        height: '20px',
        backgroundColor: 'white',
        boxShadow: '0 0 4px rgba(0,0,0,0.8)'
      }}
    />
  </div>
));

// Demographics loading overlay component
const DemographicsLoadingOverlay = React.memo(() => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      pointerEvents: 'auto'
    }}
  >
    <div
      style={{
        width: '60px',
        height: '60px',
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderTop: '4px solid #ffffff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
    <div
      style={{
        color: 'white',
        marginTop: '20px',
        fontSize: '16px',
        fontWeight: '500',
        textAlign: 'center'
      }}
    >
      Loading Demographics Data...
    </div>
    <div
      style={{
        color: 'rgba(255, 255, 255, 0.8)',
        marginTop: '8px',
        fontSize: '14px',
        textAlign: 'center'
      }}
    >
      Please wait while we fetch the demographic information
    </div>
  </div>
));

// Layer Manager Component - provides clean interface for layer components
const LayerManager = React.memo(({ children, onLayersChange }) => {
  const [layerComponents, setLayerComponents] = useState(new Map());
  
  const addLayers = useCallback((componentId, layers) => {
    setLayerComponents(prev => {
      const newMap = new Map(prev);
      newMap.set(componentId, layers);
      return newMap;
    });
  }, []);
  
  const removeLayers = useCallback((componentId) => {
    setLayerComponents(prev => {
      const newMap = new Map(prev);
      newMap.delete(componentId);
      return newMap;
    });
  }, []);
  
  // Combine all layers from different components
  const allLayers = useMemo(() => {
    const combined = [];
    for (const layers of layerComponents.values()) {
      combined.push(...layers);
    }
    return combined;
  }, [layerComponents]);
  
  // Notify parent when layers change
  useEffect(() => {
    onLayersChange(allLayers);
  }, [allLayers, onLayersChange]);
  
  // Provide layer management context to children
  const layerContext = useMemo(() => ({
    addLayers,
    removeLayers
  }), [addLayers, removeLayers]);
  
  return (
    <LayerContext.Provider value={layerContext}>
      {children}
    </LayerContext.Provider>
  );
});

// Create context for layer management
const LayerContext = React.createContext();

function DoorHopperMap({ prospects, newProspect, setNewProspect, leadType, setShowDetails, setSelectedProspect }) {
  const mapInstance = useMap();
  
  // Listen for zoom changes
  useEffect(() => {
    if (!mapInstance) return;
    
    const handleZoomChanged = () => {
      const zoomLevel = mapInstance.getZoom();
      // Switch map type based on zoom level
      if (zoomLevel >= 17) {
        mapInstance.setMapTypeId('satellite');
      } else {
        mapInstance.setMapTypeId('roadmap');
      }
    };
    
    // Add zoom change listener
    const listener = mapInstance.addListener('zoom_changed', handleZoomChanged);
    
    // Log initial zoom level
    const initialZoom = mapInstance.getZoom();
    console.log('🗺️ DOORHOPPERMAP: Initial zoom level', {
      zoomLevel: initialZoom,
      timestamp: new Date().toISOString(),
      mapType: mapInstance.getMapTypeId?.() || 'unknown'
    });
    
    return () => {
      if (listener) listener.remove();
    };
  }, [mapInstance]);

  // Extract map state - isolated from main component re-renders
  const mapState = useMapState();
  const {
    map,
    getMapCenter,
    getZoomLevel,
    debouncedZoomLevel,
    debouncedBounds
  } = mapState;

  // Extract demographics state
  const demographicsState = useDemographicsState();
  const {
    demographicsActive,
    setDemographicsActive
  } = demographicsState;

  // Optimize context usage - only extract what we need
  const context = React.useContext(AppCTX);
  const {
    user,
    teamData,
    setNewProspect: contextSetNewProspect,
    newProspect: contextNewProspect,
    setStatus,
    appointments,
    setOpenAddProspect,
    setGetPropertyDetails,
    fetchPropertyInsights,
    addAddress,
    propertyInsightsLoading
  } = context;

  // Core component state (non-map related)
  const [locations, setLocations] = useState([]);
  const [areaManagerActive, setAreaManagerActive] = useState(false);
  const [drawingLayers, setDrawingLayers] = useState([]);
  const [demographicsLayers, setDemographicsLayers] = useState([]);
  const [deckOverlay, setDeckOverlay] = useState(null);
  
  const areaManagerRef = useRef(null);

  // Track re-renders
  const renderCount = useRef(0);
  renderCount.current += 1;
  
  console.log('🔄 DOORHOPPERMAP: Re-render #', renderCount.current, {
    timestamp: new Date().toISOString(),
    props: {
      prospectsCount: prospects?.length,
      hasNewProspect: !!newProspect,
      leadType,
      hasSetShowDetails: !!setShowDetails,
      hasSetSelectedProspect: !!setSelectedProspect
    },
    state: {
      demographicsActive,
      areaManagerActive,
      hasBounds: !!debouncedBounds,
      locationsCount: locations?.length
    }
  });

  // Extract clustering logic - isolated from main component re-renders
  const clusteringState = useClustering(locations, debouncedZoomLevel, debouncedBounds);
  const {
    markerData,
    supercluster,
    clusterPoints,
    singlePoints
  } = clusteringState;



  // Memoize context values to prevent unnecessary re-renders
  const memoizedContext = useMemo(() => ({
    user,
    teamData,
    appointments,
    setOpenAddProspect,
    setGetPropertyDetails,
    addAddress,
    propertyInsightsLoading
  }), [user, teamData, appointments, setOpenAddProspect, setGetPropertyDetails, addAddress, propertyInsightsLoading]);

  // Use props if provided, otherwise use context
  const finalProspects = prospects || [];
  const finalNewProspect = newProspect || contextNewProspect;
  const finalSetNewProspect = setNewProspect || contextSetNewProspect;

  // State to track if user requested insights while address was being fetched
  const [pendingInsightsRequest, setPendingInsightsRequest] = useState(false);

  // Auto-trigger insights when address becomes available after user requested it
  useEffect(() => {
    if (pendingInsightsRequest && finalNewProspect?.address) {
      console.log('Address now available, fetching insights for:', finalNewProspect.address);
      setPendingInsightsRequest(false);
      fetchPropertyInsights({
        streetAddress: finalNewProspect.address,
        city: '',
        state: '',
        zipCode: ''
      });
    }
  }, [finalNewProspect?.address, pendingInsightsRequest, fetchPropertyInsights]);

  // Update locations when prospects or appointments change - memoized
  useEffect(() => {
    const updatedLocations = [...finalProspects, ...(memoizedContext.appointments || [])];
    setLocations(updatedLocations);
  }, [finalProspects, memoizedContext.appointments]);

  // Auto-deactivate demographics when area manager becomes active
  useEffect(() => {
    if (areaManagerActive && demographicsActive) {
      console.log('🔄 DOORHOPPERMAP: Area manager activated, deactivating demographics overlay');
      setDemographicsActive(false);
    }
  }, [areaManagerActive, demographicsActive]);

  // Clear demographics layers when demographics becomes inactive
  useEffect(() => {
    if (!demographicsActive && demographicsLayers.length > 0) {
      console.log('🔄 DOORHOPPERMAP: Demographics deactivated, clearing layers', {
        layerCount: demographicsLayers.length
      });
      setDemographicsLayers([]);
    }
  }, [demographicsActive, demographicsLayers.length]);

  // Memoized location update handler
  const handleLocationUpdate = useCallback((location) => {
    console.log('🔄 DOORHOPPERMAP: Location updated', { location });
    // No need to update map state - the map will handle this internally
    // The MyLocationMarker component will handle the actual map updates
  }, []);

  // Memoized map click handler
  const handleMapClick = useCallback((e) => {
    // Don't create new prospects when demographics layer is active
    if (demographicsActive) {
      console.log('[DoorHopperMap] Map click ignored - demographics layer is active');
      return;
    }

    if (leadType === 0) {
      const newMarker = {
        lat: e.detail.latLng.lat,
        lng: e.detail.latLng.lng
      };
      const update = { ...finalNewProspect };
      update.coords = newMarker;
      finalSetNewProspect(update);
    }
  }, [leadType, finalNewProspect, finalSetNewProspect, demographicsActive]);

  // Memoized marker click handler
  const handleMarkerClick = useCallback((lead) => {
    if (setSelectedProspect) {
      setSelectedProspect(lead);
    }
    if (setShowDetails) {
      setShowDetails(true);
    }
  }, [setSelectedProspect, setShowDetails]);

  // Memoized map click handler
  const mapClickHandler = useCallback((e) => {
    // Don't handle map clicks when demographics layer is active
    if (demographicsActive) {
      console.log('[DoorHopperMap] Map click ignored - demographics layer is active');
      return;
    }

    if (!areaManagerActive) {
      handleMapClick(e);
    }
    // Pass to AreaManager for drawing
    if (areaManagerRef.current && areaManagerRef.current.handleMapClickFromParent) {
      areaManagerRef.current.handleMapClickFromParent(e);
    }
  }, [areaManagerActive, handleMapClick, demographicsActive]);

  // Memoized hover handler for demographics
  const demographicsHoverHandler = useCallback((info) => {
    // setHoverInfo(info); // No longer managing hover state
  }, []);

  // Memoized double-click handler
  const mapDoubleClickHandler = useCallback((e) => {
    // Pass to AreaManager for drawing
    if (areaManagerRef.current && areaManagerRef.current.handleDoubleClickFromParent) {
      areaManagerRef.current.handleDoubleClickFromParent();
    }
  }, []);

  // Memoized cluster click handler
  const handleClusterClick = useCallback((info) => {
    if (info.object && info.object.properties.cluster_id && map) {
      const expansionZoom = supercluster.getClusterExpansionZoom(info.object.properties.cluster_id);
      map.setZoom(expansionZoom);
      map.panTo({lat: info.object.geometry.coordinates[1], lng: info.object.geometry.coordinates[0]});
    }
  }, [map, supercluster]);

  // --- OPTIMIZED LAYERS ---
  const clusterShadowLayer = useMemo(() =>
    new ScatterplotLayer({
      id: 'cluster-shadows',
      data: clusterPoints,
      pickable: false,
      getPosition: d => d.geometry.coordinates,
      getRadius: d => 38 + d.properties.point_count * 2.5,
      getFillColor: [0, 0, 0, 60], // subtle black shadow
      getLineColor: [0,0,0,0],
      radiusMinPixels: 36,
      radiusMaxPixels: 80,
      opacity: 0.5,
      stroked: false,
    }),
    [clusterPoints]
  );

  const clusterLayer = useMemo(() =>
    new ScatterplotLayer({
      id: 'clusters',
      data: clusterPoints,
      pickable: true,
      getPosition: d => d.geometry.coordinates,
      getRadius: d => 28 + d.properties.point_count * 2,
      getFillColor: d => {
        // Opacity increases with cluster size (max 230)
        const minAlpha = 120, maxAlpha = 230, minCount = 2, maxCount = 50;
        const count = d.properties.point_count;
        const alpha = Math.min(maxAlpha, minAlpha + Math.round((Math.min(count, maxCount) - minCount) * (maxAlpha - minAlpha) / (maxCount - minCount)));
        return [0, 0, 0, alpha];
      },
      getLineColor: [255,255,255,255], // white border
      getLineWidth: 5,
      radiusMinPixels: 28,
      radiusMaxPixels: 64,
      opacity: 1,
      stroked: true,
      onClick: handleClusterClick
    }),
    [clusterPoints, handleClusterClick]
  );

  // Only keep shadow and cluster circle layers for clusters
  const clusterLayers = useMemo(() => [
    clusterShadowLayer,
    clusterLayer
  ], [clusterShadowLayer, clusterLayer]);

  const iconLayer = useMemo(() =>
    new IconLayer({
      id: 'prospect-icons',
      data: singlePoints.map(d => d.properties),
      pickable: true,
      iconAtlas: '/spritesheet.png',
      iconMapping,
      getIcon: d => d.markerStatus,
      sizeScale: 1,
      getPosition: d => d.position,
      getSize: 40,
      onClick: info => handleMarkerClick(info.object.data),
    }),
    [singlePoints, handleMarkerClick]
  );

  // Polygon center dots now handled by TeamAssignmentLayers in AreaManager

  // Optimized layers array - polygon layers now handled by AreaManager
  const layers = useMemo(() => {
    const baseLayers = [];
    // Add cluster layers first (bottom layer)
    baseLayers.push(...clusterLayers);
    baseLayers.push(iconLayer);
    // Add drawing layers (includes team assignment polygons from AreaManager)
    baseLayers.push(...drawingLayers);
    // Add demographics layers on top
    baseLayers.push(...demographicsLayers);
    return baseLayers;
  }, [clusterLayers, iconLayer, drawingLayers, demographicsLayers]);

  // Memoized map type based on zoom
  const mapTypeId = useMemo(() => {
    const currentZoom = getZoomLevel();
    return currentZoom > 13 ? 'satellite' : 'roadmap';
  }, [getZoomLevel]);

  // Memoized new prospect marker
  const newProspectMarker = useMemo(() => {
    if (!finalNewProspect?.coords) return null;
    
    return (
      <AdvancedMarker
        key={`new-${finalNewProspect.coords.lat}-${finalNewProspect.coords.lng}`}
        position={finalNewProspect.coords}
        title="New Prospect"
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          style={{ display: 'block' }}
        >
          <g>
            <ellipse cx="20" cy="30" rx="7" ry="3" fill="#000" opacity="0.2"/>
            <path
              d="M20 4C13.3726 4 8 9.37258 8 16C8 25 20 36 20 36C20 36 32 25 32 16C32 9.37258 26.6274 4 20 4Z"
              fill="#111"
              stroke="#fff"
              strokeWidth="2"
            />
            <circle cx="20" cy="16" r="5" fill="#fff" />
          </g>
        </svg>
      </AdvancedMarker>
    );
  }, [finalNewProspect?.coords]);

  // Handler for insights button
  const handleGetInsights = useCallback(() => {
    // Check if we have coordinates but no address yet
    if (finalNewProspect?.coords && !finalNewProspect?.address) {
      console.log('Requesting insights - address will be fetched first');
      setPendingInsightsRequest(true);
      // Address will be auto-fetched by useEffect, then insights will be triggered
    } else if (finalNewProspect?.address) {
      // Address already available, get insights directly
      console.log('Getting insights for address:', finalNewProspect.address);
      
      // Extract zip code from address
      const zipMatch = finalNewProspect.address.match(/\b\d{5}\b/);
      const zipCode = zipMatch ? zipMatch[0] : '';
      
      fetchPropertyInsights({
        streetAddress: finalNewProspect.address,
        city: '',
        state: '',
        zipCode: zipCode
      });
    } else {
      // No coordinates or address available
      setStatus && setStatus({ 
        notificationType: 'error', 
        message: 'Please click on a location first to get property insights.' 
      });
    }
  }, [finalNewProspect, fetchPropertyInsights, setStatus]);

  // Memoized control buttons
  const controlButtons = useMemo(() => {
    if (!finalNewProspect?.coords) return null;
    
    return (
      <Stack spacing={2} id="map-right-controls">
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
          <IconButton 
            onClick={handleGetInsights} 
            id="get-insights-btn" 
            aria-label="insights" 
            size="large"
            disabled={memoizedContext.propertyInsightsLoading}
            title={memoizedContext.propertyInsightsLoading ? "Loading property insights..." : "Get property insights"}
          >
            {memoizedContext.propertyInsightsLoading ? (
              <div style={{ width: 24, height: 24, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            ) : (
              <HelpOutlineIcon />
            )}
          </IconButton>
        </Slide>
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
          <IconButton 
            id="add-prospect-btn" 
            onClick={() => memoizedContext.setOpenAddProspect(true)} 
            aria-label="add prospect" 
            size="large"
            title="Add new prospect"
          >
            <AddHomeIcon />
          </IconButton>
        </Slide>
      </Stack>
    );
  }, [finalNewProspect?.coords, handleGetInsights, memoizedContext.setOpenAddProspect, memoizedContext.propertyInsightsLoading]);

  // Memoized toggle handler
  const demographicsToggleHandler = useCallback(() => {
    setDemographicsActive(!demographicsActive);
  }, [demographicsActive]);

  // Memoized demographics layer management callback
  const demographicsLayerCallback = useCallback((layers) => {
    console.log('🔄 DOORHOPPERMAP: Demographics layers callback', {
      layerCount: layers?.length || 0,
      demographicsActive
    });
    setDemographicsLayers(layers || []);
  }, [demographicsActive]);

  // Memoized demographics overlay props - only update when bounds change significantly
  const demographicsOverlayProps = useMemo(() => ({
    isActive: demographicsActive,
    areaManagerActive,
    onToggle: demographicsToggleHandler,
    onLayersChange: demographicsLayerCallback
  }), [demographicsActive, areaManagerActive, demographicsToggleHandler, demographicsLayerCallback]);

  // Debug: Log when demographics props change
  useEffect(() => {
    console.log('🔄 DOORHOPPERMAP: Demographics props changed', {
      demographicsActive,
      areaManagerActive
    });
  }, [demographicsActive, areaManagerActive]);

  return (
    <>
      <Map
        style={{width: '100vw', height: '100vh'}}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={20}
        mapId="a17e7eea60bf87045627d131"
        mapTypeId={mapTypeId}
        gestureHandling="greedy"
        disableDefaultUI={true}
        onClick={mapClickHandler}
        onDoubleClick={mapDoubleClickHandler}
        options={{
          // Disable Google Maps tooltips and popups
          clickableIcons: false,
          // Disable POI (Points of Interest) tooltips
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
        {/* My Location Marker - now handles all location logic */}
        <MyLocationMarker 
          mapCenter={getMapCenter()}
          defaultCenter={DEFAULT_CENTER}
          onLocationUpdate={handleLocationUpdate}
        />

        {/* Deck.gl Overlay as per vis.gl docs */}
        <DeckGlOverlay 
          layers={layers} 
          onDeckReady={setDeckOverlay}
        />

        {/* AreaManager for area assignment/drawing */}
        <AreaManager 
          zoomLevel={getZoomLevel()} 
          areaManagerActive={areaManagerActive} 
          setAreaManagerActive={setAreaManagerActive}
          onDrawingLayersChange={setDrawingLayers}
          ref={areaManagerRef}
        />

        {/* New Prospect Marker - memoized */}
        {newProspectMarker}

        {/* Control buttons - memoized */}
        {controlButtons}
      </Map>

      {/* Demographics Toggle Button - Always show when not active and area manager is not active */}
      {!demographicsActive && !areaManagerActive && (
        <div
          style={{
            position: 'absolute',
            top: '80px',
            left: '20px',
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '25px',
            padding: '12px 20px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            userSelect: 'none'
          }}
          onClick={() => setDemographicsActive(true)}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.95)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.2)';
          }}
          title="Enable Demographics Overlay"
        >
          Demographics
        </div>
      )}

      {/* Demographics Overlay - Only mount when active AND area manager is NOT active */}
      {demographicsActive && !areaManagerActive && (
        <MemoizedDemographicsOverlay {...demographicsOverlayProps} />
      )}
    </>
  );
}

export default React.memo(DoorHopperMap);